/**
 * The words on the clinic page and the Booking tab (docs/ux/clinic.md): the
 * door words for consent and the questionnaire, the attendance marks, the
 * confirmed words, and the week arithmetic (Monday to Sunday, the browser's
 * calendar, which is Perth's for the demo).
 */

import type { ClinicRow } from '@/app/types';
import { localDateKey, longDate } from '@/lib/format';

export const CONSENT_WORD: Record<ClinicRow['consent'], string> = { returned: 'Returned', in_clinic: 'In clinic', sent: 'Sent', none: 'None' };
export const QUESTIONNAIRE_WORD: Record<ClinicRow['questionnaire'], string> = { complete: 'Complete', in_clinic: 'In clinic', none: 'None' };
export const ATTENDANCE_WORD: Record<NonNullable<ClinicRow['attendance']>, string> = { attended: 'Attended', dna: 'Did not attend', cancelled: 'Cancelled' };

export function confirmedWord(confirmed: boolean): string {
  return confirmed ? 'Confirmed' : 'Not confirmed';
}

/** The row's mark: the attendance if the appointment is over, else the flags that matter at the door. */
export function markWords(row: ClinicRow): string[] {
  const words: string[] = [];
  if (row.attendance) words.push(ATTENDANCE_WORD[row.attendance]);
  if (row.flags.urgent) words.push('Urgent');
  if (row.flags.priorDna) words.push('Prior DNA');
  if (row.flags.repeatDna) words.push('Repeat DNA');
  if (row.flags.lateCancellation) words.push('Late cancellation');
  return words;
}

/** The Monday of the week holding `day` (local calendar). */
export function mondayOf(day: Date): Date {
  const d = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  return d;
}

export function addDays(day: Date, n: number): Date {
  const d = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  d.setDate(d.getDate() + n);
  return d;
}

export function parseWeek(param: string | null): Date {
  if (param && /^\d{4}-\d{2}-\d{2}$/.test(param)) {
    const d = new Date(`${param}T00:00:00`);
    if (!Number.isNaN(d.getTime())) return mondayOf(d);
  }
  return mondayOf(new Date());
}

export const weekKey = (monday: Date): string => localDateKey(monday);
export const weekLabel = (monday: Date): string => `Week of ${longDate(monday.toISOString())}`;
export const isThisWeek = (monday: Date): boolean => weekKey(monday) === weekKey(mondayOf(new Date()));

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const dayHeading = (day: Date): string => `${DAY_NAMES[(day.getDay() + 6) % 7]} ${day.getDate()}`;
export const dayKey = (iso: string): string => localDateKey(new Date(iso));

export function timeOf(iso: string): string {
  return new Intl.DateTimeFormat('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true }).format(new Date(iso));
}

/**
 * Attendance can be marked from the row on the appointment's day or after it
 * (OPEN 80, provisional): the matter waits at 5 and no mark is recorded. The
 * engine still reads the matter's state before the move.
 */
export function canMarkAttended(row: ClinicRow, now: Date = new Date()): boolean {
  return row.status === 5 && row.attendance === null && dayKey(row.startsAt) <= localDateKey(now);
}

/** The days shown: Monday to Friday. Beam's clinics do not run at the weekend (D-0066, closing OPEN 81); a weekend appointment, should one be keyed, is still in the List view. */
export function weekdays(monday: Date): Date[] {
  return Array.from({ length: 5 }, (_, i) => addDays(monday, i));
}

/** The lanes of the week view: every clinic in this week's rows, alphabetical (D-0066). */
export function clinicLanes(rows: ClinicRow[]): Array<{ id: string; name: string }> {
  const seen = new Map<string, string>();
  for (const r of rows) if (!seen.has(r.clinic.id)) seen.set(r.clinic.id, r.clinic.name);
  return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}
